import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import UserServices from "../services/externalServices/userServices";
import RiderServices from "../services/externalServices/riderServices";
import NotificationServices from "../services/externalServices/notificationServices";

const Notification = () => {
  const [tokens, setTokens] = useState([]); // kept for backward compatibility
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [message, setMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [groupFilter, setGroupFilter] = useState([]); // array of strings
  const [interestFilter, setInterestFilter] = useState([]); // array of strings
  const [searchQuery, setSearchQuery] = useState("");

  // Helpers to read IDs, group names and interests safely
  const getUserId = (u) =>
    (u &&
      (typeof u._id === "string"
        ? u._id
        : u?._id?.toString?.() || u?._id?.$oid || u?._id)) ||
    u?.fcm_token ||
    u?.phone_number ||
    Math.random().toString(36).slice(2);

  const getGroupNames = (u) => {
    if (!u || !Array.isArray(u.groups)) return [];
    return u.groups
      .filter(Boolean)
      .map((g) =>
        // Prefer authority when present, else fallbacks
        g?.authority ||
          g?.name ||
          g?.title ||
          g?.label ||
          g?.groupName ||
          g?.id ||
          (typeof g === "string" ? g : "")
      )
      .filter(Boolean);
  };

  const getInterests = (u) =>
    Array.isArray(u?.interests) ? u.interests.filter(Boolean) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    // When filters/search are active, restrict sending to currently filtered set
    const hasActiveFilter =
      groupFilter.length > 0 || interestFilter.length > 0 || (searchQuery || "").trim().length > 0;
    const scope = hasActiveFilter ? filteredUsers : users;

    // Build tokens from selected users within scope
    const selectedTokens = scope
      .filter((u) => selectedIds.has(getUserId(u)))
      .map((u) => u.fcm_token)
      .filter(Boolean);

    const notificationData = { tokens: selectedTokens, title, body };

    try {
      // const response = await axios.post(
      //   "http://localhost:8080/sendNotification",
      //   {
      //     tokens,
      //     title,
      //     body,
      //   }
      // );
      const response = await NotificationServices.sendNotification(
        notificationData
      );
      setMessage(
        `Notification sent successfully to ${selectedTokens.length} user(s)!`
      );
      console.log("Notification sent successfully!", response.data);
    } catch (error) {
      setMessage("Error sending notification.");
      console.error(error);
    }
  };

  const getUsers = async () => {
    try {

      setLoading(true);
      setError("");

      const users = await UserServices.getAllUsers();
      // const users=await RiderServices.getDrivers();
      console.log("users===================", users);
      // Preselect all users who have fcm_token
      const initialSelected = new Set(
        users
          .filter((u) => !!u?.fcm_token)
          .map((u) => getUserId(u))
          .filter(Boolean)
      );
      setSelectedIds(initialSelected);
      setUsers(users);
      const tokensOnly = users
        .map((user) => user.fcm_token)
        .filter((token) => token);
      setTokens(tokensOnly); // maintain original state for backward compatibility
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getUsers();
  }, []);

  // Build option lists for filters
  const allGroupOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => getGroupNames(u).forEach((g) => set.add(g)));
    return Array.from(set).sort();
  }, [users]);

  const allInterestOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => getInterests(u).forEach((i) => set.add(i)));
    return Array.from(set).sort();
  }, [users]);

  // Apply filters
  const filteredUsers = useMemo(() => {
    const query = (searchQuery || "").trim();
    let regex = null;
    if (query) {
      try {
        regex = new RegExp(query, "i");
      } catch (e) {
        regex = null;
      }
    }
    return users.filter((u) => {
      const groups = getGroupNames(u);
      const interests = getInterests(u);

      const groupMatch =
        groupFilter.length === 0 ||
        groupFilter.every((gf) => groups.includes(gf));

      const interestMatch =
        interestFilter.length === 0 ||
        interestFilter.every((iflt) => interests.includes(iflt));

      // Free-text/regex search across key fields
      const name = `${(u.first_name || "").trim()} ${(u.last_name || "").trim()}`.trim();
      const phone = `${u.countryCode ? `+${u.countryCode}` : ""} ${u.phone_number || ""}`.trim();
      const email = `${u.email_id || ""}`;
      const haystack = `${name} | ${phone} | ${email} | ${groups.join(" ")} | ${
        interests.join(" ")
      }`;
      const searchMatch = !query
        ? true
        : regex
        ? regex.test(haystack)
        : haystack.toLowerCase().includes(query.toLowerCase());

      return groupMatch && interestMatch && searchMatch;
    });
  }, [users, groupFilter, interestFilter, searchQuery]);

  const allFilteredIds = useMemo(
    () => new Set(filteredUsers.map((u) => getUserId(u))),
    [filteredUsers]
  );

  const allFilteredSelectableIds = useMemo(
    () =>
      new Set(
        filteredUsers
          .filter((u) => !!u?.fcm_token)
          .map((u) => getUserId(u))
      ),
    [filteredUsers]
  );

  const isAllFilteredSelected = useMemo(() => {
    if (allFilteredSelectableIds.size === 0) return false;
    for (const id of allFilteredSelectableIds) {
      if (!selectedIds.has(id)) return false;
    }
    return true;
  }, [allFilteredSelectableIds, selectedIds]);

  const toggleSelectAllFiltered = () => {
    const next = new Set(selectedIds);
    if (isAllFilteredSelected) {
      // Unselect only those currently filter-visible and selectable
      for (const id of allFilteredSelectableIds) next.delete(id);
    } else {
      for (const id of allFilteredSelectableIds) next.add(id);
    }
    setSelectedIds(next);
  };

  const clearAllSelection = () => setSelectedIds(new Set());

  const toggleRow = (u) => {
    const id = getUserId(u);
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const resetFilters = () => {
    setGroupFilter([]);
    setInterestFilter([]);
    setSearchQuery("");
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom className="text-slate-800">
        Send Notification
      </Typography>

      {/* Top action bar - sticky so send is always accessible */}
      <Box className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} p={2} alignItems="center">
          <Button
            type="button"
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={selectedIds.size === 0}
            className="shadow"
          >
            Send Notification to Selected
          </Button>
          <Typography variant="body2" className="text-slate-600">
            Selected: {selectedIds.size} / {users.length}
          </Typography>
          <Typography variant="body2" className="text-slate-600">
            Filtered: {filteredUsers.length}
          </Typography>
          <Typography variant="caption" className="text-slate-500">
            Will send to users with FCM token only
          </Typography>
        </Stack>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2} mt={2}>
        <TextField
          fullWidth
          label="Search (regex or text)"
          placeholder="name, phone, interests, groups"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel id="group-filter-label">Filter by Groups</InputLabel>
          <Select
            labelId="group-filter-label"
            multiple
            value={groupFilter}
            label="Filter by Groups"
            onChange={(e) => setGroupFilter(e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {allGroupOptions.map((g) => (
              <MenuItem key={g} value={g}>
                <Checkbox checked={groupFilter.indexOf(g) > -1} />
                <Typography variant="body2">{g}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="interest-filter-label">
            Filter by Interests
          </InputLabel>
          <Select
            labelId="interest-filter-label"
            multiple
            value={interestFilter}
            label="Filter by Interests"
            onChange={(e) => setInterestFilter(e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {allInterestOptions.map((i) => (
              <MenuItem key={i} value={i}>
                <Checkbox checked={interestFilter.indexOf(i) > -1} />
                <Typography variant="body2">{i}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" onClick={resetFilters}>
            Reset Filters
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />
        <TextField
          label="Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
        />
      </Stack>

      <Stack direction="row" spacing={1} mb={1} alignItems="center">
        <Button variant="outlined" onClick={toggleSelectAllFiltered}>
          {isAllFilteredSelected
            ? "Unselect All (Filtered)"
            : "Select All (Filtered)"}
        </Button>
        <Button variant="text" color="secondary" onClick={clearAllSelection}>
          Clear Selection
        </Button>
      </Stack>

      <TableContainer component={Paper} className="notification-table-container rounded-lg shadow-sm">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedIds.size > 0 && !isAllFilteredSelected &&
                    allFilteredSelectableIds.size > 0
                  }
                  checked={isAllFilteredSelected}
                  onChange={toggleSelectAllFiltered}
                  inputProps={{ "aria-label": "select all filtered" }}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell className="whitespace-nowrap" sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>Phone</TableCell>
              <TableCell>Interests</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Last Accessed</TableCell>
              <TableCell>Token</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>Loading users…</TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={9}>{error}</TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>No users match filters</TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((u) => {
                const id = getUserId(u);
                const hasToken = !!u?.fcm_token;
                const selected = selectedIds.has(id);
                const interests = getInterests(u);
                const groups = getGroupNames(u);
                return (
                  <TableRow key={id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        disabled={!hasToken}
                        onChange={() => toggleRow(u)}
                        inputProps={{
                          "aria-label": hasToken
                            ? "select user"
                            : "no token available",
                          title: hasToken ? "" : "No FCM token",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {(u.first_name || "").trim()} {(u.last_name || "").trim()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap" sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                      {u.countryCode ? `+${u.countryCode}` : ""} {u.phone_number || ""}
                    </TableCell>
                    <TableCell>
                      {interests.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {interests.map((i) => (
                            <Chip key={i} label={i} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {groups.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {groups.map((g) => (
                            <Chip key={g} label={g} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.is_active ? (
                        <Chip label="Active" color="success" size="small" />
                      ) : (
                        <Chip label="Inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {u.lastAccessed
                        ? new Date(u.lastAccessed).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {hasToken ? (
                        <Chip label="Has Token" color="primary" size="small" />
                      ) : (
                        <Chip label="No Token" variant="outlined" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {message && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default Notification;





